import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Building2, 
  Shield, 
  LogOut, 
  Plus, 
  Eye,
  CheckCircle,
  XCircle,
  LayoutTemplate,
  Users,
  Key,
  Settings,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import TemplateManagement from '@/components/TemplateManagement';
import ProfileSettings from '@/components/ProfileSettings';

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { logout, isSuperAdmin, selectTenant, selectedTenantId, clearTenantSelection } = useAuth();
  const [activeTab, setActiveTab] = useState('tenants'); // 'tenants' or 'templates' or 'profile'
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTenantForm, setShowCreateTenantForm] = useState(false);
  const [showCreateAccountDialog, setShowCreateAccountDialog] = useState(false);
  const [showViewAccountsDialog, setShowViewAccountsDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showDeleteTenantDialog, setShowDeleteTenantDialog] = useState(false);
  const [selectedTenantForAccount, setSelectedTenantForAccount] = useState(null);
  const [selectedTenantForDelete, setSelectedTenantForDelete] = useState(null);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [schoolAccounts, setSchoolAccounts] = useState([]);
  const [accountForm, setAccountForm] = useState({ username: '', password: '' });
  const [resetPasswordForm, setResetPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [newCredentials, setNewCredentials] = useState(null);
  const [currentUsername, setCurrentUsername] = useState('');
  const [newTenant, setNewTenant] = useState({
    school_name: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/');
      return;
    }
    fetchCurrentUser();
    fetchTenants();
  }, [isSuperAdmin, navigate]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUsername(response.data.username);
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/super-admin/tenants`);
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.school_name.trim()) {
      toast.error('School name is required');
      return;
    }

    try {
      await axios.post(`${API}/super-admin/tenants`, newTenant);
      toast.success('School created successfully');
      setShowCreateTenantForm(false);
      setNewTenant({ school_name: '', contact_email: '', contact_phone: '' });
      fetchTenants();
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to create school');
    }
  };

  const handleActivateTenant = async (tenantId) => {
    try {
      await axios.put(`${API}/super-admin/tenants/${tenantId}/activate`);
      toast.success('School activated successfully');
      fetchTenants();
    } catch (error) {
      console.error('Error activating tenant:', error);
      toast.error('Failed to activate school');
    }
  };

  const handleDeactivateTenant = async (tenantId) => {
    try {
      await axios.put(`${API}/super-admin/tenants/${tenantId}/deactivate`);
      toast.success('School deactivated successfully');
      fetchTenants();
    } catch (error) {
      console.error('Error deactivating tenant:', error);
      toast.error('Failed to deactivate school');
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenantForDelete) return;
    
    try {
      const response = await axios.delete(`${API}/super-admin/tenants/${selectedTenantForDelete.id}`);
      toast.success(response.data.message || 'School deleted successfully');
      setShowDeleteTenantDialog(false);
      setSelectedTenantForDelete(null);
      fetchTenants();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete school');
    }
  };

  const handleViewSchool = (tenantId) => {
    selectTenant(tenantId);
    toast.success('Viewing school data');
    // Navigate to the school admin view with selected tenant
    navigate('/school-admin');
  };

  const fetchSchoolAccounts = async (tenantId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/auth/school-accounts/${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setSchoolAccounts(response.data);
    } catch (error) {
      console.error('Error fetching school accounts:', error);
      toast.error('Failed to load school accounts');
    }
  };

  const handleViewAccounts = async (tenantId) => {
    setSelectedTenantForAccount(tenantId);
    await fetchSchoolAccounts(tenantId);
    setShowViewAccountsDialog(true);
  };

  const handleCreateSchoolAccount = async () => {
    if (!accountForm.username.trim() || !accountForm.password.trim()) {
      toast.error('Username and password are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/auth/create-school-account?tenant_id=${selectedTenantForAccount}&username=${encodeURIComponent(accountForm.username)}&password=${encodeURIComponent(accountForm.password)}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Show credentials
      setNewCredentials({
        username: accountForm.username,
        password: accountForm.password,
        school: tenants.find(t => t.id === selectedTenantForAccount)?.school_name
      });
      
      toast.success('School account created successfully!');
      setShowCreateAccountDialog(false);
      setAccountForm({ username: '', password: '' });
    } catch (error) {
      console.error('Error creating school account:', error);
      toast.error(error.response?.data?.detail || 'Failed to create school account');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordForm.newPassword.trim()) {
      toast.error('New password is required');
      return;
    }

    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/auth/reset-school-password?user_id=${selectedUserForReset.id}&new_password=${encodeURIComponent(resetPasswordForm.newPassword)}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Show new credentials
      setNewCredentials({
        username: selectedUserForReset.username,
        password: resetPasswordForm.newPassword,
        school: tenants.find(t => t.id === selectedUserForReset.tenant_id)?.school_name
      });
      
      toast.success('Password reset successfully!');
      setShowResetPasswordDialog(false);
      setResetPasswordForm({ newPassword: '', confirmPassword: '' });
      
      // Refresh accounts list
      if (selectedTenantForAccount) {
        await fetchSchoolAccounts(selectedTenantForAccount);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Super Admin Dashboard
                </h1>
                <p className="text-sm text-slate-600">Akansha EduWorld</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-6">
        <div className="flex space-x-2 border-b">
          <button
            onClick={() => setActiveTab('tenants')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'tenants'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Building2 className="w-4 h-4" />
              <span>Manage Schools</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'templates'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <LayoutTemplate className="w-4 h-4" />
              <span>Manage Templates</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'profile'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Settings className="w-4 h-4" />
              <span>Profile</span>
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'tenants' && (
          <div>
            {/* Create Tenant Button */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">Schools / Tenants</h2>
              <Button
                onClick={() => setShowCreateTenantForm(true)}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New School
              </Button>
            </div>

            {/* Create Tenant Form */}
            {showCreateTenantForm && (
              <Card className="p-6 mb-6 border-2 border-indigo-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Create New School</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      School Name *
                    </label>
                    <input
                      type="text"
                      value={newTenant.school_name}
                      onChange={(e) => setNewTenant({ ...newTenant, school_name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholder="Enter school name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={newTenant.contact_email}
                      onChange={(e) => setNewTenant({ ...newTenant, contact_email: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholder="contact@school.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={newTenant.contact_phone}
                      onChange={(e) => setNewTenant({ ...newTenant, contact_phone: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholder="+1234567890"
                    />
                  </div>
                  <div className="flex space-x-3 pt-2">
                    <Button
                      onClick={handleCreateTenant}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                    >
                      Create School
                    </Button>
                    <Button
                      onClick={() => {
                        setShowCreateTenantForm(false);
                        setNewTenant({ school_name: '', contact_email: '', contact_phone: '' });
                      }}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Tenants List */}
            {loading ? (
              <div className="text-center py-12 text-slate-500">Loading schools...</div>
            ) : tenants.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No schools created yet. Create your first school to get started.
              </div>
            ) : (
              <div className="grid gap-4">
                {tenants.map((tenant) => (
                  <Card key={tenant.id} className="p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                          <Building2 className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">{tenant.school_name}</h3>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-slate-600">
                            {tenant.contact_email && <span>{tenant.contact_email}</span>}
                            {tenant.contact_phone && <span>{tenant.contact_phone}</span>}
                          </div>
                          <div className="flex items-center space-x-2 mt-2">
                            <span
                              className={`text-xs px-2 py-1 rounded-full font-medium ${
                                tenant.status === 'active'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {tenant.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                            <span className="text-xs text-slate-500">
                              Created: {new Date(tenant.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => handleViewAccounts(tenant.id)}
                          variant="outline"
                          size="sm"
                          className="flex items-center space-x-1 text-purple-600 hover:text-purple-700"
                        >
                          <Users className="w-4 h-4" />
                          <span>View Accounts</span>
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedTenantForAccount(tenant.id);
                            setShowCreateAccountDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="flex items-center space-x-1 text-blue-600 hover:text-blue-700"
                        >
                          <Key className="w-4 h-4" />
                          <span>Create Account</span>
                        </Button>
                        <Button
                          onClick={() => handleViewSchool(tenant.id)}
                          variant="outline"
                          size="sm"
                          className="flex items-center space-x-1"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View Data</span>
                        </Button>
                        {tenant.status === 'active' ? (
                          <Button
                            onClick={() => handleDeactivateTenant(tenant.id)}
                            variant="outline"
                            size="sm"
                            className="flex items-center space-x-1 text-orange-600 hover:text-orange-700"
                          >
                            <XCircle className="w-4 h-4" />
                            <span>Deactivate</span>
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleActivateTenant(tenant.id)}
                            variant="outline"
                            size="sm"
                            className="flex items-center space-x-1 text-green-600 hover:text-green-700"
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Activate</span>
                          </Button>
                        )}
                        {tenant.school_name !== 'Default School' && (
                          <Button
                            onClick={() => {
                              setSelectedTenantForDelete(tenant);
                              setShowDeleteTenantDialog(true);
                            }}
                            variant="outline"
                            size="sm"
                            className="flex items-center space-x-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <TemplateManagement />
        )}

        {activeTab === 'profile' && (
          <ProfileSettings currentUsername={currentUsername} />
        )}
      </div>

      {/* Create School Account Dialog */}
      <Dialog open={showCreateAccountDialog} onOpenChange={setShowCreateAccountDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create School Account</DialogTitle>
            <DialogDescription>
              Create login credentials for{' '}
              {tenants.find(t => t.id === selectedTenantForAccount)?.school_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="account-username">Username *</Label>
              <Input
                id="account-username"
                placeholder="e.g., pacificworld"
                value={accountForm.username}
                onChange={(e) => setAccountForm({ ...accountForm, username: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="account-password">Password *</Label>
              <Input
                id="account-password"
                type="password"
                placeholder="Enter secure password"
                value={accountForm.password}
                onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
              />
            </div>
            <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded">
              ⚠️ Make sure to share these credentials securely with the school admin
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateAccountDialog(false);
                setAccountForm({ username: '', password: '' });
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateSchoolAccount}>
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View School Accounts Dialog */}
      <Dialog open={showViewAccountsDialog} onOpenChange={setShowViewAccountsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>School Accounts</DialogTitle>
            <DialogDescription>
              Login accounts for {tenants.find(t => t.id === selectedTenantForAccount)?.school_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {schoolAccounts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No accounts created yet for this school</p>
                <Button
                  onClick={() => {
                    setShowViewAccountsDialog(false);
                    setShowCreateAccountDialog(true);
                  }}
                  className="mt-4"
                  size="sm"
                >
                  Create Account
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {schoolAccounts.map((account) => (
                  <Card key={account.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{account.username}</p>
                        <p className="text-sm text-slate-500">
                          Created: {new Date(account.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-amber-600 mt-1">
                          🔒 Password is encrypted and cannot be retrieved
                        </p>
                      </div>
                      <Button
                        onClick={() => {
                          setSelectedUserForReset(account);
                          setShowViewAccountsDialog(false);
                          setShowResetPasswordDialog(true);
                        }}
                        size="sm"
                        variant="outline"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Key className="w-4 h-4 mr-1" />
                        Reset Password
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowViewAccountsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Create a new password for <strong>{selectedUserForReset?.username}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-password">New Password *</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password"
                value={resetPasswordForm.newPassword}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm Password *</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Confirm new password"
                value={resetPasswordForm.confirmPassword}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
              />
            </div>
            <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded">
              ⚠️ Make sure to share the new password securely with the school admin
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowResetPasswordDialog(false);
                setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                setShowViewAccountsDialog(true);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleResetPassword}>
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Credentials Display Dialog */}
      {newCredentials && (
        <Dialog open={!!newCredentials} onOpenChange={() => setNewCredentials(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>✅ Credentials Created</DialogTitle>
              <DialogDescription>
                Share these credentials securely with {newCredentials.school}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Card className="p-4 bg-blue-50 border-2 border-blue-200">
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-700">School</Label>
                    <p className="font-bold text-lg">{newCredentials.school}</p>
                  </div>
                  <div>
                    <Label className="text-slate-700">Username</Label>
                    <p className="font-mono text-lg font-bold text-blue-700">{newCredentials.username}</p>
                  </div>
                  <div>
                    <Label className="text-slate-700">Password</Label>
                    <p className="font-mono text-lg font-bold text-blue-700">{newCredentials.password}</p>
                  </div>
                </div>
              </Card>
              <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded">
                ⚠️ <strong>Important:</strong> Copy these credentials now. The password cannot be retrieved later.
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setNewCredentials(null)}>
                I've Saved the Credentials
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Tenant Confirmation Dialog */}
      <Dialog open={showDeleteTenantDialog} onOpenChange={setShowDeleteTenantDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete School</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedTenantForDelete?.school_name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-red-800">⚠️ Warning: This action cannot be undone!</p>
              <p className="text-sm text-red-700">
                Deleting this school will permanently remove:
              </p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                <li>All departments</li>
                <li>All KRAs and KPIs</li>
                <li>All reviews and performance data</li>
                <li>All user accounts associated with this school</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteTenantDialog(false);
                setSelectedTenantForDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteTenant}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete School
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
